import { getProperties, getPropertiesByHostId } from 'utils/apis/property';
import { getUnits } from 'utils/apis/unit';
import { getServicePackageUnits } from 'utils/apis/servicePackage';

const hostIdOf = property => {
  if (!property) return undefined;
  if (property.host && typeof property.host === 'object') {
    return property.host._id || property.host.id;
  }
  return property.host;
};

const propertyFromUnit = unit => {
  const property = unit && unit.roomType && unit.roomType.property;
  if (!property) return null;
  if (typeof property === 'object') {
    return { _id: property._id, name: property.name || String(property._id), host: property.host };
  }
  return { _id: property, name: String(property) };
};

const propertiesByHost = {};
const hostUnitsByHost = {};
const unitsByProperty = {};
const inflightProperties = {};
const inflightServicePackageUnits = {};
const inflightUnitsForProperty = {};

const hasCache = (store, key) => Object.prototype.hasOwnProperty.call(store, key);

const fetchServicePackageUnitsOnce = hostId => {
  const key = String(hostId);
  if (hasCache(hostUnitsByHost, key)) {
    return Promise.resolve({ status: 200, data: hostUnitsByHost[key] });
  }
  if (inflightServicePackageUnits[key]) return inflightServicePackageUnits[key];
  inflightServicePackageUnits[key] = getServicePackageUnits(hostId)
    .then(res => {
      if (res && res.status === 200 && Array.isArray(res.data)) {
        hostUnitsByHost[key] = res.data;
      }
      return res;
    })
    .finally(() => {
      delete inflightServicePackageUnits[key];
    });
  return inflightServicePackageUnits[key];
};

const fetchHostPropertiesUncached = async hostId => {
  try {
    const byHost = await getPropertiesByHostId(hostId);
    if (Array.isArray(byHost) && byHost.length) return byHost;
  } catch (e) {
    // fall through
  }

  try {
    const res = await fetchServicePackageUnitsOnce(hostId);
    const units = (res && res.status === 200 && Array.isArray(res.data) && res.data) || [];
    if (units.length) {
      const byId = {};
      units.forEach(unit => {
        const property = propertyFromUnit(unit);
        if (property && property._id) {
          byId[String(property._id)] = property;
        }
      });
      const properties = Object.keys(byId).map(id => byId[id]);
      if (properties.length) return properties;
    }
  } catch (e) {
    // fall through
  }

  try {
    const all = await getProperties({ fields: ['_id', 'name', 'host'] });
    const list = Array.isArray(all) ? all : [];
    return list.filter(p => String(hostIdOf(p)) === String(hostId));
  } catch (e) {
    return [];
  }
};

/**
 * Load properties for a host. Cached per host until the host changes
 * (or the SPA reloads). Shared by Expenses / Revenue / Instalments / Reporting.
 */
export const loadHostProperties = async hostId => {
  if (!hostId) return [];
  const key = String(hostId);
  if (hasCache(propertiesByHost, key)) return propertiesByHost[key];
  if (inflightProperties[key]) return inflightProperties[key];
  inflightProperties[key] = fetchHostPropertiesUncached(hostId)
    .then(list => {
      const properties = list || [];
      propertiesByHost[key] = properties;
      return properties;
    })
    .finally(() => {
      delete inflightProperties[key];
    });
  return inflightProperties[key];
};

export const loadUnitsForProperty = async propertyId => {
  if (!propertyId) return [];
  const key = String(propertyId);
  if (hasCache(unitsByProperty, key)) return unitsByProperty[key];
  if (inflightUnitsForProperty[key]) return inflightUnitsForProperty[key];
  inflightUnitsForProperty[key] = getUnits(propertyId)
    .then(list => {
      const units = Array.isArray(list) ? list : [];
      unitsByProperty[key] = units;
      return units;
    })
    .catch(() => [])
    .finally(() => {
      delete inflightUnitsForProperty[key];
    });
  return inflightUnitsForProperty[key];
};

/**
 * Load all units for a host (via servicePackage units endpoint).
 * Cached per host and shared across tabs until the host changes.
 */
export const loadHostUnits = async hostId => {
  if (!hostId) return [];
  const key = String(hostId);
  if (hasCache(hostUnitsByHost, key)) return hostUnitsByHost[key];
  try {
    const res = await fetchServicePackageUnitsOnce(hostId);
    if (res && res.status === 200 && Array.isArray(res.data)) {
      return res.data;
    }
  } catch (e) {
    // ignore
  }
  return [];
};
